import React, { useState, useEffect, useContext } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Download, DateRange, Assessment } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useProcess } from '../context/ProcessContext';
import { NotificationContext } from '../context/NotificationContext';
import HelpInfoIcon from '../components/HelpInfoIcon';
import { API_ENDPOINTS, apiCall } from '../config/api';
import FavoriteButton from '../components/FavoriteButton';

const SAF1909InventoryAnalysis = () => {
  const theme = useTheme();
  const { processes, addProcess, updateProcess, removeProcess } = useProcess();
  const { addStructuredNotification } = useContext(NotificationContext);

  // State management
  const [startDate, setStartDate] = useState(new Date(2025, 3, 1)); // April 1, 2025
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loadingLastUpdated, setLoadingLastUpdated] = useState(true);

  // Minimum date constraint (April 1, 2025)
  const minDate = new Date(2025, 3, 1);
  const maxDate = new Date();

  // Fetch last updated information on component mount
  useEffect(() => {
    fetchLastUpdated();
  }, []);

  const fetchLastUpdated = async () => {
    try {
      const data = await apiCall(API_ENDPOINTS.SAF1909_GET_LAST_UPDATED, {
        method: 'GET',
      });

      setLastUpdated(data.lastUpdated);
    } catch (err) {
      console.error('Error fetching last updated:', err);
      setLastUpdated('Error fetching date');
    } finally {
      setLoadingLastUpdated(false);
    }
  };

  // Handle downloading yearly out-of-stock summary
  const handleDownloadYearlySummary = async () => {
    const processId = addProcess({
      name: 'SAF1909 Yearly Summary Download',
      status: 'running',
      startTime: Date.now(),
    });

    try {
      setError(null);

      const response = await apiCall(API_ENDPOINTS.SAF1909_DOWNLOAD_YEARLY_SUMMARY, {
        method: 'POST',
      });

      // Handle file download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Get filename from response header or use default
      const disposition = response.headers.get('Content-Disposition');
      let fileName = 'SAF1909_Yearly_Summary.csv';
      if (disposition) {
        const filenameMatch = disposition.match(/filename[^;=\\n]*=((['\"]).*?\\2|[^;\\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          fileName = filenameMatch[1].replace(/['\"]/g, '');
        }
      }

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      updateProcess(processId, { status: 'success' });

      // Send success notification
      if (addStructuredNotification) {
        try {
          await addStructuredNotification('SAF1909_DOWNLOAD_SUCCESS', {
            fileName: fileName,
            reportType: 'Yearly Summary',
            downloadTime: Date.now() - (processes.find(p => p.id === processId)?.startTime || Date.now())
          });
        } catch (error) {
          console.error('Error sending success notification:', error);
        }
      }

      setTimeout(() => removeProcess(processId), 2000);

    } catch (err) {
      updateProcess(processId, { status: 'error', error: err.message });
      setError(`Error downloading yearly summary: ${err.message}`);

      // Send error notification
      if (addStructuredNotification) {
        try {
          await addStructuredNotification('SAF1909_DOWNLOAD_FAILED', {
            reportType: 'Yearly Summary',
            error: err.message
          }, true);
        } catch (error) {
          console.error('Error sending error notification:', error);
        }
      }

      setTimeout(() => removeProcess(processId), 5000);
    }
  };

  // Handle generating custom out-of-stock report
  const handleGenerateReport = async () => {
    // Validate dates
    if (startDate > endDate) {
      setError('Start date must be before end date.');
      return;
    }

    const processId = addProcess({
      name: 'SAF1909 Custom Report Generation',
      status: 'running',
      startTime: Date.now(),
    });

    try {
      setError(null);

      const requestData = {
        startDate: startDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
        endDate: endDate.toISOString().split('T')[0],
      };

      const response = await apiCall(API_ENDPOINTS.SAF1909_GENERATE_REPORT, {
        method: 'POST',
        body: JSON.stringify(requestData),
      });

      // Handle CSV file download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create filename with date range
      const startStr = startDate.toISOString().split('T')[0].replace(/-/g, '');
      const endStr = endDate.toISOString().split('T')[0].replace(/-/g, '');
      const fileName = `OutOfStockReport_${startStr}_${endStr}.csv`;

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      updateProcess(processId, { status: 'success' });

      // Send success notification
      if (addStructuredNotification) {
        try {
          await addStructuredNotification('SAF1909_REPORT_SUCCESS', {
            fileName: fileName,
            reportType: 'Custom Out-of-Stock Report',
            dateRange: `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
            downloadTime: Date.now() - (processes.find(p => p.id === processId)?.startTime || Date.now())
          });
        } catch (error) {
          console.error('Error sending success notification:', error);
        }
      }

      setTimeout(() => removeProcess(processId), 2000);

    } catch (err) {
      updateProcess(processId, { status: 'error', error: err.message });
      setError(`Error generating report: ${err.message}`);

      // Send error notification
      if (addStructuredNotification) {
        try {
          await addStructuredNotification('SAF1909_REPORT_FAILED', {
            reportType: 'Custom Out-of-Stock Report',
            dateRange: `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
            error: err.message
          }, true);
        } catch (error) {
          console.error('Error sending error notification:', error);
        }
      }

      setTimeout(() => removeProcess(processId), 5000);
    }
  };

  // Check if any SAF1909 processes are running
  const isProcessRunning = processes.some(p => 
    p.name.includes('SAF1909') && p.status === 'running'
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto', position: 'relative' }}>
        <FavoriteButton interfaceName="SAF1909 Inventory Analysis" category="excel" />
        <HelpInfoIcon page="SAF1909InventoryAnalysis" />
        
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 700,
            fontSize: '1.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.1rem',
            textShadow: '1px 1px 3px rgba(0, 0, 0, 0.1)',
            borderBottom: `2px solid #228B2233`,
            pb: 1,
            mb: 4,
          }}
        >
          SAF1909 Inventory Analysis
        </Typography>

        <Typography
          variant="body2"
          sx={{ 
            color: theme.palette.text.secondary, 
            mb: 4, 
            textAlign: 'center', 
            fontStyle: 'italic' 
          }}
        >
          Download yearly summaries or generate custom out-of-stock reports for inventory analysis
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Yearly Summary Section */}
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                height: '100%',
                border: `2px solid #228B2220`,
                '&:hover': {
                  borderColor: '#228B2240',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assessment sx={{ color: '#228B22', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Yearly Summary
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                Download the pre-generated yearly out-of-stock summary report
              </Typography>

              <Button
                variant="contained"
                startIcon={isProcessRunning ? <CircularProgress size={20} /> : <Download />}
                onClick={handleDownloadYearlySummary}
                disabled={isProcessRunning}
                sx={{
                  backgroundColor: '#228B22',
                  color: 'white',
                  mb: 2,
                  '&:hover': {
                    backgroundColor: '#1F7A1F',
                  },
                  '&:disabled': {
                    backgroundColor: theme.palette.grey[400],
                  },
                  width: '100%',
                  py: 1.5,
                }}
              >
                {isProcessRunning ? 'Downloading...' : 'Download Yearly Summary'}
              </Button>

              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {loadingLastUpdated ? (
                  <CircularProgress size={16} />
                ) : (
                  `Last Updated: ${lastUpdated || 'Unknown'}`
                )}
              </Typography>
            </Paper>
          </Grid>

          {/* Custom Report Section */}
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                height: '100%',
                border: `2px solid #228B2220`,
                '&:hover': {
                  borderColor: '#228B2240',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DateRange sx={{ color: '#228B22', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Custom Report
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                Generate out-of-stock reports for a specific date range
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Select Date Range:
              </Typography>

              <Box sx={{ mb: 2 }}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  minDate={minDate}
                  maxDate={maxDate}
                  renderInput={(params) => 
                    <TextField {...params} fullWidth size="small" />
                  }
                  sx={{ mb: 2 }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  minDate={minDate}
                  maxDate={maxDate}
                  renderInput={(params) => 
                    <TextField {...params} fullWidth size="small" />
                  }
                />
              </Box>

              <Button
                variant="contained"
                startIcon={isProcessRunning ? <CircularProgress size={20} /> : <Download />}
                onClick={handleGenerateReport}
                disabled={isProcessRunning || !startDate || !endDate}
                sx={{
                  backgroundColor: '#228B22',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1F7A1F',
                  },
                  '&:disabled': {
                    backgroundColor: theme.palette.grey[400],
                  },
                  width: '100%',
                  py: 1.5,
                }}
              >
                {isProcessRunning ? 'Generating...' : 'Generate Report'}
              </Button>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
            Report Information
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            • Yearly Summary: Pre-generated comprehensive out-of-stock analysis<br />
            • Custom Report: CSV export with ItemId, OutOfStockDays, and TotalDaysInRange<br />
            • Date range restricted to April 1, 2025 onwards
          </Typography>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default SAF1909InventoryAnalysis;